import { useState, type MouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import Avatar from '@mui/material/Avatar'
import Box from '@mui/material/Box'
import ButtonBase from '@mui/material/ButtonBase'
import CircularProgress from '@mui/material/CircularProgress'
import Divider from '@mui/material/Divider'
import ListItemText from '@mui/material/ListItemText'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import UnfoldMoreRoundedIcon from '@mui/icons-material/UnfoldMoreRounded'
import { useAuth } from '../contexts/AuthContext'
import { useCondominium } from '../contexts/CondominioContext'
import { fetchAllPages } from '../utils/fetchAllPages'
import type { EstateResponse } from '../utils/types'

function initials(name: string) {
  return name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase()
}

/**
 * Propriedade activa no topo do menu lateral (substitui a faixa azul que
 * aparecia em todas as páginas). Mudar de condomínio é para quem está ligado a
 * mais do que um; "Ver todos" (sair do contexto) continua exclusivo do ADMIN.
 */
export default function PropertySwitcher({ collapsed = false }: { collapsed?: boolean }) {
  const { t } = useTranslation(['nav', 'condominiums'])
  const navigate = useNavigate()
  const { activeCondominium, clearActiveCondominium } = useCondominium()
  const { user, switchEstate, exitEstate, hasPermission } = useAuth()
  const isAdmin = hasPermission('ESTATE_CONTEXT_EXIT')
  const canSwitch = (user?.estateIds?.length ?? 0) > 1 || isAdmin

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  // `null` = ainda por carregar. Vem de /api/estates (a mesma fonte do
  // /properties): o ADMIN recebe todos os estates do tenant e os restantes
  // papéis só os seus.
  const [options, setOptions] = useState<EstateResponse[] | null>(null)
  const [failed, setFailed] = useState(false)

  if (!activeCondominium) return null
  const name = activeCondominium.nome
  const units = activeCondominium.totalUnidades ?? activeCondominium.totalUnidadesCadastradas

  const open = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setFailed(false)
    // Recarrega a cada abertura para apanhar condomínios criados/desactivados entretanto.
    fetchAllPages<EstateResponse>('/api/estates', { suppressForbiddenToast: true })
      // switch-estate só aceita estates activos, e este módulo só gere condomínios.
      .then((estates) => setOptions(estates.filter((e) => e.stateType === 'CONDOMINIUM' && e.status === 'ACTIVE')))
      .catch(() => setFailed(true))
  }
  const close = () => setAnchorEl(null)

  const select = async (targetId: string) => {
    close()
    if (targetId === activeCondominium.id) return
    await switchEstate(targetId)
    navigate(`/properties/${targetId}`)
  }

  const viewAll = async () => {
    close()
    clearActiveCondominium()
    if (isAdmin) await exitEstate() // remove o estate activo do token JWT; exit é exclusivo do ADMIN
    navigate('/properties')
  }

  const avatar = (
    <Avatar variant="rounded" sx={{ width: 36, height: 36, bgcolor: 'primary.main', fontSize: '0.8125rem', fontWeight: 800, borderRadius: '10px' }}>
      {initials(name)}
    </Avatar>
  )

  const card = (
    <ButtonBase
      onClick={canSwitch ? open : undefined}
      disabled={!canSwitch}
      aria-haspopup={canSwitch ? 'menu' : undefined}
      aria-expanded={canSwitch ? Boolean(anchorEl) : undefined}
      aria-label={canSwitch ? `${t('nav:property.switch')}: ${name}` : undefined}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: 1.25,
        p: collapsed ? 0.75 : 1.25,
        justifyContent: collapsed ? 'center' : 'flex-start',
        borderRadius: '12px',
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        textAlign: 'left',
        '&:hover': canSwitch ? { borderColor: 'brand.teal500', bgcolor: 'surface.subtle' } : undefined,
        '&.Mui-disabled': { opacity: 1 },
      }}
    >
      {avatar}
      {!collapsed && (
        <>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 800, color: 'text.primary' }}>{name}</Typography>
            <Typography variant="caption" color="text.secondary" component="p" sx={{ lineHeight: 1.35 }}>
              {t('nav:property.type')}
              {units != null && <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{` · ${t('nav:property.units', { count: units })}`}</Box>}
            </Typography>
          </Box>
          {canSwitch && <UnfoldMoreRoundedIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
        </>
      )}
    </ButtonBase>
  )

  return (
    <>
      {collapsed ? <Tooltip title={name} placement="right">{card}</Tooltip> : card}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'left', vertical: 'top' }}
        slotProps={{ paper: { sx: { minWidth: 240, mt: 0.5 } } }}
      >
        {options === null ? (
          <MenuItem disabled sx={{ gap: 1 }}>
            {failed ? t('nav:property.loadError') : <><CircularProgress size={16} /> {t('nav:property.loading')}</>}
          </MenuItem>
        ) : (
          options.map((c) => (
            <MenuItem key={c.id} selected={c.id === activeCondominium.id} onClick={() => select(c.id)}>
              <ListItemText primary={c.name} secondary={c.id === activeCondominium.id ? t('nav:property.current') : undefined} />
            </MenuItem>
          ))
        )}
        {isAdmin && <Divider />}
        {isAdmin && (
          <MenuItem onClick={viewAll}>
            <ListItemText primary={t('condominiums:context.viewAll')} />
          </MenuItem>
        )}
      </Menu>
    </>
  )
}
